import { useCallback, useEffect, useState } from "react";
import { getGroupColor, INHERITED_ALPHA, toStyle } from "../../styles";
import GeoJSON from "ol/format/GeoJSON";
import { Feature, Map } from "ol";
import { useVectorLayer } from "../utils/useVectorLayer";
import { FeatureLike } from "ol/Feature";
import { transformExtent } from "ol/proj";
import { request } from "@/request";
import getBackendURL from "@/api/api";

const HARGIS_BASE =
	"https://maps.dnr.illinois.gov/portal/apps/webappviewer/index.html?id=8f6e15ca8973412bbd534e6990da752d";
const PLSS_BASE =
	"https://www.arcgis.com/apps/webappviewer/index.html?id=16239dfab62f49e48e692bb93b495fd9";

export function useQuestionLayer(
	map: Map | null,
	activeId: string,
	questionId: string,
	shapeVersion: number,
	visible: (feature: FeatureLike) => boolean,
	baseColor: string,
) {
	const { source, layer } = useVectorLayer(map, {
		zIndex: 2,
		style: (feature) => {
			const stroke = getGroupColor(feature.get("group"));
			const fill = stroke + "aa";
			return stroke
				? toStyle(fill, stroke)
				: toStyle(`${baseColor}33`, baseColor);
		},
	});

	const [hargisUrl, setHargisUrl] = useState("");
	const [plssUrl, setPlssUrl] = useState("");
	const [mapLoading, setMapLoading] = useState(false);
	// The ESR these shapes actually came from — the backend walks back to the
	// most recent version at or before `activeId`, so it may be an earlier one.
	const [showingFrom, setShowingFrom] = useState("");
	// The loaded shapes in the order the backend returned them, so the caller
	// can step through them one at a time — the source itself has no order
	const [shapes, setShapes] = useState<Feature[]>([]);
	// The one shape on show, or -1 for all of them. Only what's drawn changes;
	// the source keeps every shape for the list, the legend and editing.
	const [shapeIndex, setShapeIndex] = useState(-1);

	useEffect(() => {
		if (!source || !activeId || !questionId) return;

		let cancelled = false;
		const url = getBackendURL(`/esr/geodata/${activeId}/${questionId}/`);

		setMapLoading(true);
		request(url).then(({ data }) => {
			if (cancelled) return;
			setMapLoading(false);
			try {
				const format = new GeoJSON({
					dataProjection: "EPSG:4326",
					featureProjection: "EPSG:3857",
				});
				source.clear();
				setShowingFrom(String(data.esr_id));
				const features = format.readFeatures(data.geojson) as Feature[];

				if (features !== undefined) {
					// A different set of shapes, so start from all of them again
					setShapes(features);
					setShapeIndex(-1);
					source.addFeatures(features);

					// Compute HARGIS/PLSS URLs directly from the loaded data — no OL event round-trip needed
					const rawExtent = source.getExtent();
					if (rawExtent.every(Number.isFinite)) {
						const ext = transformExtent(rawExtent, "EPSG:3857", "EPSG:4326");
						const extentParam = `&extent=${ext[0]},${ext[1]},${ext[2]},${ext[3]}`;
						const findParam = `&find=${(ext[0] + ext[2]) / 2},${(ext[1] + ext[3]) / 2}`;
						setHargisUrl(HARGIS_BASE + extentParam + findParam);
						setPlssUrl(PLSS_BASE + extentParam + findParam);
					}
				}
			} catch (error) {
				console.error("Error processing GeoJSON:", error);
			}
		});

		return () => {
			cancelled = true;
			source.clear();
		};
	}, [shapeVersion, activeId, questionId]);

	const inherited = showingFrom !== "" && showingFrom !== activeId;

	const shapeCount = shapes.length;
	const currentShape = shapeIndex === -1 ? null : shapes[shapeIndex];

	// -1 shows all shapes; stepping past either end wraps back around to all
	const nextShape = useCallback(() => {
		setShapeIndex((prev) => (prev + 1 >= shapeCount ? -1 : prev + 1));
	}, [shapeCount]);

	const previousShape = useCallback(() => {
		setShapeIndex((prev) => (prev === -1 ? shapeCount - 1 : prev - 1));
	}, [shapeCount]);

	const showAllShapes = useCallback(() => {
		setShapeIndex(-1);
	}, []);

	// The caller's own rule (e.g. the legend's groups) narrowed to the shape on show
	const shown = useCallback(
		(feature: FeatureLike) =>
			visible(feature) && (currentShape === null || feature === currentShape),
		[visible, currentShape],
	);

	useEffect(() => {
		if (!layer) return () => {};

		layer.setStyle((feature) => {
			if (!shown(feature)) {
				return toStyle("transparent", "transparent");
			}
			const stroke = getGroupColor(feature.get("group"));
			const fill = stroke + (inherited ? INHERITED_ALPHA : "aa");
			return stroke
				? toStyle(fill, stroke)
				: toStyle(baseColor + (inherited ? INHERITED_ALPHA : "66"), baseColor);
		});

		return () => {
			layer.setStyle(null);
		};
	}, [shown, inherited]);

	return {
		source,
		layer,
		hargisUrl,
		plssUrl,
		mapLoading,
		showingFrom,
		inherited,
		shown,
		shapeIndex,
		shapeCount,
		currentShape,
		nextShape,
		previousShape,
		showAllShapes,
	};
}
